import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Info, Lock, Search } from 'lucide-react'
import { Button } from '../Button'
import { Input } from '../Input'
import { Skeleton } from '../Skeleton'
import { Tab, Tabs } from '../Tabs'
import { Drawer, DrawerBody, DrawerContent, DrawerFooter, DrawerHeader } from '../Drawer'
import { ScorecardCards } from './ScorecardCards'
import { ScorecardMatrix } from './ScorecardMatrix'
import { MarkSwatch, formatPoints } from './marks'
import type { ScorecardClimber, ScorecardData, ScorecardRoute } from './types'

/** The key of the "every route" tab, and of routes live for the whole run. */
export const ALL_ROUTES = 'all'
const WHOLE_RUN = 'whole-run'
const WIDE_QUERY = '(min-width: 640px)'

export interface CompetitionScorecardProps {
  data: ScorecardData | null
  loading?: boolean
  failed?: boolean
  /** The signed-in climber, whose card is pinned and highlighted. */
  myUserId?: string
  /** Avatar and name, in each app's own way. */
  renderClimber: (climber: ScorecardClimber) => ReactNode
  /** The gym app opens the member's profile from here. */
  onOpenClimber?: (climber: ScorecardClimber) => void
  /** Where the round tabs stick, below whatever header the app pins. */
  stickyTopClassName?: string
}

/** Phone or not. A table of 40 columns has no business on a 390px screen. */
function useWide(): boolean {
  const [wide, setWide] = useState(
    () => typeof window !== 'undefined' && !!window.matchMedia?.(WIDE_QUERY)?.matches,
  )
  useEffect(() => {
    const query = window.matchMedia?.(WIDE_QUERY)
    if (!query) return
    const onChange = () => setWide(query.matches)
    query.addEventListener?.('change', onChange)
    return () => query.removeEventListener?.('change', onChange)
  }, [])
  return wide
}

interface RouteGroup {
  key: string
  name: string
  state?: 'upcoming' | 'in_progress' | 'ended'
  routes: ScorecardRoute[]
}

/**
 * Every climber in one category against every route: ✕ for a top, ⚡ for a
 * flash, with what each route is worth, so a total can be checked by hand.
 *
 * Phones get one card per climber with a grid of numbered tiles, and a tap
 * turns the card into the sum. Wider screens get the full matrix with the name
 * and total columns pinned.
 */
export function CompetitionScorecard({
  data,
  loading,
  failed,
  myUserId,
  renderClimber,
  onOpenClimber,
  stickyTopClassName = 'top-0',
}: CompetitionScorecardProps) {
  const { t } = useTranslation()
  const wide = useWide()
  const [query, setQuery] = useState('')
  const [drawer, setDrawer] = useState<
    { kind: 'route'; routeId: string } | { kind: 'rule' } | null
  >(null)
  const [focus, setFocus] = useState<{ userId: string; tick: number } | null>(null)

  // One tab per round that has routes, in the gym's order, then the routes that
  // are live for the whole competition, if any.
  const groups = useMemo<RouteGroup[]>(() => {
    if (!data) return []
    const byRound: RouteGroup[] = data.rounds
      .map((round) => ({
        key: round.id,
        name: round.name,
        state: round.state,
        routes: data.routes.filter((r) => r.roundId === round.id),
      }))
      .filter((g) => g.routes.length > 0)
    const wholeRun = data.routes.filter(
      (r) => r.roundId === null || !data.rounds.some((round) => round.id === r.roundId),
    )
    if (wholeRun.length)
      byRound.push({
        key: WHOLE_RUN,
        name: t('scorecard.roundWholeRun'),
        state: undefined,
        routes: wholeRun,
      })
    return byRound
  }, [data, t])

  // The round being climbed now, when there is one. Otherwise the whole set.
  const defaultGroup = groups.find((g) => g.state === 'in_progress')?.key ?? ALL_ROUTES
  const [groupKey, setGroupKey] = useState<string | null>(null)
  const selected =
    groupKey && (groupKey === ALL_ROUTES || groups.some((g) => g.key === groupKey))
      ? groupKey
      : defaultGroup

  const routes = useMemo(() => {
    if (!data) return []
    if (selected === ALL_ROUTES) return groups.flatMap((g) => g.routes)
    return groups.find((g) => g.key === selected)?.routes ?? []
  }, [data, groups, selected])

  const climbers = useMemo(() => {
    const all = data?.climbers ?? []
    const q = query.trim().toLocaleLowerCase()
    if (!q) return all
    return all.filter((c) =>
      [c.displayName, c.fullName, c.nickname].some((s) => s?.toLocaleLowerCase().includes(q)),
    )
  }, [data, query])

  const me = myUserId ? data?.climbers.find((c) => c.userId === myUserId) : undefined
  const roundName =
    selected === ALL_ROUTES ? null : (groups.find((g) => g.key === selected)?.name ?? null)

  if (failed) {
    return (
      <p className="py-10 text-center text-sm text-foreground/60">{t('scorecard.loadFailed')}</p>
    )
  }
  if (loading && !data) {
    return (
      <div className="flex flex-col gap-3" aria-busy="true">
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} className="h-28 rounded-2xl" />
        ))}
      </div>
    )
  }
  if (!data || data.climbers.length === 0) {
    return <p className="py-10 text-center text-sm text-foreground/60">{t('scorecard.empty')}</p>
  }

  const drawerRoute =
    drawer?.kind === 'route' ? data.routes.find((r) => r.routeId === drawer.routeId) : undefined

  function jumpToMe() {
    if (!me) return
    setQuery('')
    const userId = me.userId
    setFocus((f) => ({ userId, tick: (f?.tick ?? 0) + 1 }))
  }

  return (
    <div className="flex flex-col gap-3">
      <div
        className={`sticky ${stickyTopClassName} z-20 -mx-1 flex flex-col gap-2 bg-background/95 px-1 py-2 backdrop-blur`}
      >
        {groups.length > 1 && (
          <Tabs
            aria-label={t('scorecard.roundsLabel')}
            size="sm"
            fullWidth={!wide}
            selectedKey={selected}
            onSelectionChange={(key) => setGroupKey(String(key))}
            classNames={{
              base: 'max-w-full',
              tabList: 'max-w-full overflow-x-auto scrollbar-hide',
            }}
          >
            {[
              <Tab key={ALL_ROUTES} title={t('scorecard.roundAll')} />,
              ...groups.map((g) => (
                <Tab
                  key={g.key}
                  title={
                    <span className="flex items-center gap-1.5 whitespace-nowrap">
                      {g.state === 'in_progress' && (
                        <span
                          className="size-1.5 rounded-full bg-primary"
                          aria-label={t('scorecard.roundLive')}
                        />
                      )}
                      {g.state === 'ended' && (
                        <Lock
                          className="size-3 opacity-50"
                          aria-label={t('scorecard.roundClosed')}
                        />
                      )}
                      {g.name}
                    </span>
                  }
                />
              )),
            ]}
          </Tabs>
        )}
        <div className="flex gap-2">
          <Input
            size="sm"
            aria-label={t('scorecard.search')}
            placeholder={t('scorecard.search')}
            value={query}
            onValueChange={setQuery}
            isClearable
            onClear={() => setQuery('')}
            startContent={<Search className="size-4 text-foreground/40" aria-hidden="true" />}
          />
          {me && (
            <Button size="sm" color="primary" className="h-auto shrink-0" onPress={jumpToMe}>
              {t('scorecard.me')}
            </Button>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-foreground/60">
          <span className="flex items-center gap-1.5">
            <MarkSwatch mark="top" />
            {t('scorecard.legendTop')}
          </span>
          {data.scoringType !== 'tops_count' && (
            <span className="flex items-center gap-1.5">
              <MarkSwatch mark="flash" />
              {t('scorecard.legendFlash')}
            </span>
          )}
          <span className="flex items-center gap-1.5">
            <MarkSwatch />
            {t('scorecard.legendNone')}
          </span>
          <Button
            size="sm"
            variant="light"
            color="primary"
            className="ml-auto h-7 min-w-0 px-2"
            startContent={<Info className="size-3.5" aria-hidden="true" />}
            onPress={() => setDrawer({ kind: 'rule' })}
          >
            {t('scorecard.howScored')}
          </Button>
        </div>
      </div>

      {climbers.length === 0 ? (
        <p className="py-8 text-center text-sm text-foreground/60">{t('scorecard.noMatch')}</p>
      ) : wide ? (
        <ScorecardMatrix
          scoringType={data.scoringType}
          routes={routes}
          climbers={climbers}
          showRoundTotal={roundName !== null}
          myUserId={myUserId}
          focus={focus}
          renderClimber={renderClimber}
          onOpenClimber={onOpenClimber}
          onOpenRoute={(routeId) => setDrawer({ kind: 'route', routeId })}
          groupStarts={new Set(groups.map((g) => g.routes[0]?.routeId).filter(Boolean))}
        />
      ) : (
        <ScorecardCards
          scoringType={data.scoringType}
          routes={routes}
          climbers={climbers}
          roundName={roundName}
          me={query ? undefined : me}
          focus={focus}
          renderClimber={renderClimber}
          onOpenClimber={onOpenClimber}
          onOpenRoute={(routeId) => setDrawer({ kind: 'route', routeId })}
        />
      )}

      <Drawer
        isOpen={drawer !== null}
        onClose={() => setDrawer(null)}
        placement={wide ? 'right' : 'bottom'}
        size={wide ? 'sm' : 'md'}
      >
        <DrawerContent>
          {drawer?.kind === 'rule' && <RuleSheet data={data} />}
          {drawerRoute && (
            <RouteSheet
              route={drawerRoute}
              scoringType={data.scoringType}
              roundName={groups.find((g) => g.routes.includes(drawerRoute))?.name}
              climbers={data.climbers}
              myUserId={myUserId}
            />
          )}
          <DrawerFooter>
            <Button variant="flat" fullWidth onPress={() => setDrawer(null)}>
              {t('scorecard.close')}
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </div>
  )
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl bg-content2 p-3">
      <p className="text-xs text-foreground/60">{label}</p>
      <p className="text-lg font-bold tabular-nums">{value}</p>
    </div>
  )
}

/** The rule in plain words, with a worked example from this very table. */
function RuleSheet({ data }: { data: ScorecardData }) {
  const { t } = useTranslation()
  if (data.scoringType === 'tops_count') {
    return (
      <>
        <DrawerHeader>{t('scorecard.ruleTitle')}</DrawerHeader>
        <DrawerBody>
          <p className="text-sm leading-relaxed">{t('scorecard.ruleTopsCount')}</p>
        </DrawerBody>
      </>
    )
  }
  // A route a handful of people have topped makes the clearest example.
  const example =
    [...data.routes].filter((r) => r.tops > 1).sort((a, b) => a.tops - b.tops)[0] ??
    data.routes.find((r) => r.tops > 0)
  return (
    <>
      <DrawerHeader>{t('scorecard.ruleTitle')}</DrawerHeader>
      <DrawerBody className="gap-4">
        <p className="text-sm leading-relaxed">{t('scorecard.ruleBody')}</p>
        {example && (
          <div className="flex flex-col gap-2">
            <p className="text-sm font-semibold">
              {t('scorecard.ruleExample', { label: example.label, count: example.tops })}
            </p>
            <div className="grid grid-cols-2 gap-2">
              <Stat
                label={t('scorecard.pointsTop')}
                value={`1000 / ${example.tops} = ${formatPoints(example.topPoints)}`}
              />
              <Stat
                label={t('scorecard.pointsFlash')}
                value={`+10% = ${formatPoints(example.flashPoints)}`}
              />
            </div>
          </div>
        )}
      </DrawerBody>
    </>
  )
}

/** One route: what it is worth and who topped it. */
function RouteSheet({
  route,
  scoringType,
  roundName,
  climbers,
  myUserId,
}: {
  route: ScorecardRoute
  scoringType: string
  roundName?: string
  climbers: ScorecardClimber[]
  myUserId?: string
}) {
  const { t } = useTranslation()
  const toppers = climbers
    .filter((c) => c.marks[route.routeId])
    .sort(
      (a, b) =>
        (a.marks[route.routeId] === 'flash' ? 0 : 1) - (b.marks[route.routeId] === 'flash' ? 0 : 1),
    )
  const split = scoringType === 'thousand_split'
  return (
    <>
      <DrawerHeader className="flex flex-col gap-1">
        <span className="flex items-center gap-2 text-xl">
          {route.color && (
            <span
              className="size-4 rounded-full ring-1 ring-black/15"
              style={{ backgroundColor: route.color }}
              aria-hidden="true"
            />
          )}
          {t('scorecard.routeLabel', { label: route.label })}
        </span>
        {roundName && <span className="text-sm font-normal text-foreground/60">{roundName}</span>}
      </DrawerHeader>
      <DrawerBody className="gap-4">
        <div className={`grid gap-2 ${split ? 'grid-cols-3' : 'grid-cols-2'}`}>
          <Stat label={t('scorecard.statTops')} value={String(route.tops)} />
          <Stat label={t('scorecard.pointsTop')} value={formatPoints(route.topPoints)} />
          {split && (
            <Stat label={t('scorecard.pointsFlash')} value={formatPoints(route.flashPoints)} />
          )}
        </div>
        <div>
          <p className="mb-1 text-sm font-semibold">{t('scorecard.toppersTitle')}</p>
          {toppers.length === 0 ? (
            <p className="py-4 text-sm text-foreground/60">
              {t(split ? 'scorecard.noToppersSplit' : 'scorecard.noToppers')}
            </p>
          ) : (
            <ul className="divide-y divide-divider">
              {toppers.map((c) => (
                <li
                  key={c.userId}
                  className={`flex items-center justify-between gap-3 py-2 text-sm ${
                    c.userId === myUserId ? 'font-semibold text-primary' : ''
                  }`}
                >
                  <span className="min-w-0 truncate">{c.displayName}</span>
                  <MarkSwatch mark={c.marks[route.routeId]} />
                </li>
              ))}
            </ul>
          )}
        </div>
      </DrawerBody>
    </>
  )
}
